import {
  rendersConfigurableDateColor,
  rendersMonthCardHeader,
  rendersTodayMarker,
} from '@/features/layouts/layout.lib';
import { EventCardLayouts } from '@/types/gql.generated';
import type {
  ExperienceChildConfigKind,
  ExperienceLayouts,
} from '@/types/gql.generated';

type ViewCapability = {
  layoutId: ExperienceLayouts;
  supportedByVibe: boolean;
};

export const shouldShowDatesInPrimaryColor = ({
  layoutId,
  supportedByVibe,
  usesVibeRenderer,
}: ViewCapability & { usesVibeRenderer: boolean }): boolean =>
  usesVibeRenderer && supportedByVibe && rendersConfigurableDateColor(layoutId);

/**
 * Whether a view offers the Bold/Light card fill.
 *
 * Two routes, gated differently, and the asymmetry is the point.
 *
 * A grid offers it wherever Card is the active display type — ungated by renderer,
 * because the classic printable templates honor colorMode too: PlannerEventItem
 * resolves the same category fill from it, so a flag-off planner sheet responds to the
 * control exactly as the block-rendered one does.
 *
 * Month Cards draws no event cards and reaches colorMode only through the vibes'
 * MonthCardHeader, which lives in the block MonthCardGrid collection. Flag off, those
 * layouts fall back to the classic yearlyMonthCard template, which reads neither the
 * vibe nor colorMode — so the control would write a setting nothing renders. Hence
 * usesVibeRenderer on this branch and not the other.
 */
export const shouldShowCardColorMode = ({
  layoutId,
  cardLayoutId,
  showDisplayType,
  usesVibeRenderer,
}: {
  layoutId: ExperienceLayouts;
  cardLayoutId: EventCardLayouts;
  showDisplayType: boolean;
  usesVibeRenderer: boolean;
}): boolean =>
  (showDisplayType && cardLayoutId === EventCardLayouts.GridEventCard) ||
  (usesVibeRenderer && rendersMonthCardHeader(layoutId));

export const shouldShowHighlightToday = ({
  kind,
  layoutId,
  supportedByVibe,
}: ViewCapability & {
  kind?: ExperienceChildConfigKind | null;
}): boolean => supportedByVibe && rendersTodayMarker(layoutId, kind);
